import { FilterService } from './services/filter.service';
// Import external
import { Component, ElementRef, EventEmitter, Inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { ActivatedRoute, Router } from '@angular/router';
import { ClipboardService } from '@app/services/clipboard.service';
import { SnackbarService } from '@app/services/snackbar.service';
import { SearchFilter } from './search-v2-filter';
import { SearchbarFacet } from './searchbar-v2-facet';
import { SearchbarFilterSaveDialogV2Component } from './searchbar-v2-filter-save-dialog.component';
import { SearchbarState } from './searchbar-v2-state';
import { FilterStorage, StorageItem } from './searchbar-v2-storage';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';


const defaultSearchbarTranslations: SearchbarTranslations = {
    DIALOG: {
        TITLE: 'Enregistrer sous',
    },
    ACTIONS: {
        MY_FILTERS: 'Mes Filtres',
        ADD_FILTER: 'Ajouter un filtre',
        FILTERS: 'Filtres',
        SEARCH: 'Rechercher',
        DELETE_ALL: 'Réinitialiser',
        SAVE_FILTER: 'Sauvegarder',
        LOAD_SELECTED_FILTER: 'Charger le filtre',
        DELETE_SELECTED_FILTER: 'Supprimer le filtre',
        DELETE_FILTERS: 'Supprimer les filtres',
        DEFAULT_FILTERS: 'Filtres par défaut',
        CANCEL: 'Annuler',
        SAVE: 'Sauvegarder'
    },
    TOOLTIPS: {
        UPDATE_FAVORITE_FILTER: 'Définir ce filtre par défaut',
        DELETE_FILTER: 'Supprimer ce filtre',
        COPY_LINK: 'Copier l\'URL',
    },
    OPERATORS: {
        EQUAL: 'Egal',
        MATCH: 'Contient',
        NOT_EQUAL: 'Différent de',
        BIGGER_THAN: 'Superieur à',
        BIGGER_OR_EQUAL_THAN: 'Superieur ou égal à',
        SMALLER_THAN: 'Inférieur à',
        SMALLER_OR_EQUAL_THAN: 'Inférieur ou égal à',
        CONTAIN_ALL: 'Contient',
        CONTAIN_NOT_ALL: 'Contient',
        IS_ONE_OF: 'Est l\'un de',
        IS_NOT_ONE_OF: 'N\'est pas l\'un de',
        HAS: 'N\'est pas vide',
        HAS_NOT: 'Est vide',
    },
    WIDGET: {
        PROPERTY: 'Propriété',
        OPERATOR: 'Operateur',
        VALUE: 'Valeur'
    }
};

export interface FilterTransformer {
    encode: (filter: SearchFilter) => string;
    decode: (filters: string) => SearchFilter[];
    encodeSearchState: (filter: SearchbarState) => string;
}

export interface SearchbarTranslations {
    ACTIONS: {
        DELETE_SELECTED_FILTER: string;
        LOAD_SELECTED_FILTER: string;
        DEFAULT_FILTERS: string;
        MY_FILTERS: string;
        SAVE_FILTER: string;
        ADD_FILTER: string;
        FILTERS: string;
        SEARCH: string;
        DELETE_ALL: string;
        CANCEL: string;
        SAVE: string;
        DELETE_FILTERS: string;
    };
    TOOLTIPS: {
        UPDATE_FAVORITE_FILTER: string;
        DELETE_FILTER: string;
        COPY_LINK: string;
    };
    WIDGET: {
        PROPERTY: string;
        OPERATOR: string;
        VALUE: string;
    };
    OPERATORS: { [key: string]: string };
    DIALOG: {
        TITLE: string;
    };
}

export interface SearchChangeEvent {
    action: 'ADD_FILTER' | 'REMOVE_FILTER' | 'REMOVE_ALL_FILTERS' | 'UPDATE_QUERY' | 'LOAD_ALL_FILTERS' | 'CLEAR_QUERY';
    state: SearchbarState;
}

export interface UpdateFavoriteEvent {
    storageItem: StorageItem;
    status: boolean;
}

export interface DeleteStorageItemEvent {
    storageItem: StorageItem;
}

@Component({
    selector: 'syn-searchbar-v2',
    templateUrl: 'searchbar-v2.component.html',
    styleUrls: ['searchbar-v2.component.scss'],
})
export class SearchbarV2Component implements OnInit, OnDestroy {

    @ViewChild('query') public query: ElementRef;
    @ViewChild('addFilterMenuTrigger', { read: MatMenuTrigger, static: true }) public trigger: MatMenuTrigger;
    @Input() public title: string;
    @Input() public defaultFilters: StorageItem[] = [];
    @Input() public facets: SearchbarFacet[] = [];
    @Input() public translations: SearchbarTranslations = defaultSearchbarTranslations;
    @Output() public searchChange: EventEmitter<SearchChangeEvent> = new EventEmitter<SearchChangeEvent>();
    @Output() public updateFavorite: EventEmitter<UpdateFavoriteEvent> = new EventEmitter<UpdateFavoriteEvent>();
    @Output() public deleteStorageItem: EventEmitter<DeleteStorageItemEvent> = new EventEmitter<DeleteStorageItemEvent>();

    public storageFilters: StorageItem[] = [];

    public searchQuery = '';

    private _state: SearchbarState = new SearchbarState();
    private readonly querySubject: Subject<string> = new Subject<string>();
    private submitSubject: Subject<Event> = new Subject<Event>();
    private filtersSubscriptions: Subscription[] = [];
    private currentFilterName = '';

    constructor(
        @Inject('FilterTransformer') private readonly filterTransformer: FilterTransformer,
        @Inject('FilterStorage') private readonly filterStorage: FilterStorage,
        public dialog: MatDialog,
        private readonly filterService: FilterService,
        private readonly route: ActivatedRoute,
        private readonly router: Router,
        private readonly snackbarService: SnackbarService,
        private readonly clipboardService: ClipboardService,
    ) {
        this.querySubject.subscribe((query: string) => this.queryChanged(query));
    }

    get state(): SearchbarState {
        return this._state;
    }

    get hasSearchQuery(): boolean {
        return !this.searchQuery;
    }

    get hasFilters() {
        return (this.state?.filters || []).length === 0;
    }

    get filters(): SearchFilter[] {
        return this._state.filters;
    }

    set filters(filters: SearchFilter[]) {
        this.filtersSubscriptions.forEach(sub => sub.unsubscribe());
        this.filtersSubscriptions = [];

        filters.forEach((searchFilter) => {
            const facet = this.facets.find(f => f.property === searchFilter.property);
            if (facet && facet.humanize) {
                const sub = facet.humanize(searchFilter.value, searchFilter.operator).subscribe(chipLabel => {
                    searchFilter.chipLabel = chipLabel;
                });
                this.filtersSubscriptions.push(sub);
            }
        });

        this._state.filters = filters;
    }

    public async ngOnInit(): Promise<void> {
        const SEARCH_DEBOUNCE_TIME_MS = 300;
        this.storageFilters = await this.filterStorage.listCustomFilters().toPromise();
        const urlQuery = this.route.snapshot.queryParamMap.get('query');
        const urlFilter = this.route.snapshot.queryParamMap.get('filter');
        const favoriteFilter = this.storageFilters.find((item) => item.isFavorite);

        if (urlQuery || urlFilter) {
            this._state.query = urlQuery || '';
            this.filters = urlFilter ? this.decodeUrlFilter(urlFilter) : [];
        } else if (favoriteFilter) {
            this.applyStorageItem(favoriteFilter);
        } else {
            this._state = this.filterService.getLastFilter();
        }
        this.searchQuery = this._state.query;
        this.searchChangeEmit({ action: 'LOAD_ALL_FILTERS' });
        this.submitSubject
            .pipe(
                debounceTime(SEARCH_DEBOUNCE_TIME_MS),
                distinctUntilChanged(),
                filter(event => !!event)
            )
            .subscribe((event: Event) => {
                event.preventDefault();
                event.stopPropagation();
                this.updateQuery(this.searchQuery);
            });
    }

    public ngOnDestroy(): void {
        this.querySubject.unsubscribe();
        this.filtersSubscriptions.forEach(sub => sub.unsubscribe());
        this.submitSubject.unsubscribe();
    }

    public submitSearch(event: Event) {
        this.submitSubject.next(event);
    }

    public updateQuery(query: string): void {
        this._state.query = query;
        this.searchChangeEmit({
            action: 'UPDATE_QUERY',
        });
    }

    public onQueryChange(value: string): void {
        this.querySubject.next(value);
    }

    public queryChanged(query: string): void {
        if (this._state.query && !query.length) {
            this.updateQuery('');
        }
    }

    public removeFilter(filterToRemove: SearchFilter): void {
        this._state.filters = this._state.filters.filter((f) => f !== filterToRemove);

        this.searchChangeEmit({
            action: 'REMOVE_FILTER',
        });
    }

    public removeAll(): void {
        this._state.filters = [];
        this.searchChangeEmit({
            action: 'REMOVE_ALL_FILTERS',
        });
    }

    public clearQuery(): void {
        this._state.query = '';
        this.searchChangeEmit({
            action: 'CLEAR_QUERY',
        });
    }

    public openFilterSaveDialog(): void {
        const dialogRef = this.dialog.open(SearchbarFilterSaveDialogV2Component, {
            width: '700px',
            data: { name: this.currentFilterName, translations: this.translations },
        });

        dialogRef.afterClosed().subscribe(async (name: string) => {
            if (name) {
                this.storageFilters = await this.filterStorage.addCustomFilter({
                    query: this.query.nativeElement.value,
                    filters: this._state.filters,
                    name,
                    updatedAt: new Date().toISOString(),
                    isFavorite: false,
                }).toPromise();
            }
        });
    }

    public copyLink(): void {
        this.clipboardService.copy(window.location.href);
    }

    public addFilter(searchFilter: SearchFilter): void {
        this.filters = [...this.filters, {
            ...searchFilter,
            query: this.filterTransformer.encode(searchFilter),
        }];
        this.searchChangeEmit({
            action: 'ADD_FILTER',
        });
    }

    public loadSelectedFilter(storageItem: StorageItem): void {
        this.applyStorageItem(storageItem);
        this.searchChangeEmit({
            action: 'LOAD_ALL_FILTERS',
        });
    }

    public async deleteSelectedFilter(storageItem: StorageItem, event: MouseEvent): Promise<void> {
        // prevent menu items closed
        event.stopPropagation();

        this.storageFilters = await this.filterStorage.removeCustomFilter(storageItem.name).toPromise();
        this.deleteStorageItem.emit({
            storageItem,
        });
    }

    public handleUpdateFavorite(storageItem: StorageItem, event: MouseEvent): void {
        // prevent menu items closed
        event.stopImmediatePropagation();

        this.filterStorage.updateFavorite(storageItem).subscribe((storageItems) => this.storageFilters = storageItems);

        this.updateFavorite.emit({
            storageItem,
            status: !storageItem.isFavorite,
        });
    }

    private applyStorageItem({ filters, name, query }: StorageItem): void {
        this.currentFilterName = name;
        this._state.query = query;
        this.filters = filters;
    }

    private decodeUrlFilter(urlFilter: string): SearchFilter[] {
        try {
            return this.filterTransformer.decode(urlFilter);
        } catch (error) {
            this.snackbarService.show({
                message: `Le filtre de l'URL est invalide : ${(error as Error).message}`,
                type: 'danger',
                timeout: 5000,
            });
            return [];
        }
    }

    private searchChangeEmit({ action }: Pick<SearchChangeEvent, 'action'>) {
        this.filterService.setLastFilter(this.state);
        this.syncUrlFilter();
        this.searchChange.emit({
            action,
            state: this._state,
        });
    }

    private syncUrlFilter(): void {
        void this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {
                query: this._state.query || null,
                filter: this.filters.map((searchFilter) => searchFilter.query).join(' ') || null,
            },
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

}
